import React from 'react';

import CallMadeRoundedIcon from '@mui/icons-material/CallMadeRounded';
import { useAsyncFn } from 'react-use';
import { observer } from 'mobx-react';

import { Alert, Link } from '@infoods/lifebase-ui';

import classes from './AddSeatsModal.module.scss';
import { LbBigModal } from '../../../shared/components/modals';
import { entityGeneralSetupService } from '../../services/entityGeneralSetupService';
import { openNewTab } from '../../../shared/utils/windowUtils';
import { useUserRoleLabel } from '../../../shared/hooks/useUserRoleLabel';
import { supportEmail, supportEmailHref } from '../../../shared/constants/supportEmail';

type AddSeatsModalProps = {
  open: boolean;
  onClose: () => void;
  entityId: number;
  coachSeats: number;
  usedSeats: number;
};

const AddSeatsModal = observer(
  ({ open, onClose, entityId, coachSeats, usedSeats }: AddSeatsModalProps) => {
    const { getForAgent } = useUserRoleLabel();
    const [state, handleUpgradePlanLink] = useAsyncFn(async () => {
      const { upgradePlanUrl } =
        await entityGeneralSetupService.getUpgradePlanUrl(entityId);

      openNewTab(upgradePlanUrl);
    }, [entityId]);

    return (
      <LbBigModal
        open={open}
        onClose={onClose}
        isLoading={state.loading}
        title="Add Seats"
        buttonOptions={{
          yesText: (
            <div className={classes.confirmButtonContent}>
              Upgrade Plan <CallMadeRoundedIcon fontSize="small" />
            </div>
          ),
          yesHandler: handleUpgradePlanLink,
          noText: 'Close',
          noHandler: onClose,
        }}
      >
        <p className={classes.description}>
          You currently are using&nbsp;
          <span className="private-info">
            {usedSeats}/{coachSeats}
          </span>
          &nbsp;seats that you are paying for.
        </p>
        <p className={classes.boldText}>
          To purchase more {getForAgent({ case: 'lower' })} seats:
        </p>
        <ul className={classes.instructionsList}>
          <li>- Click &quot;Upgrade Plan&quot; to increase your seat limit.</li>
          <li>- In GHL, click on &quot;Want to modify/cancel the subscription?&quot;</li>
          <li>- Select &quot;Upgrade&quot; and choose a plan with additional seats.</li>
          <li>
            - Email&nbsp;
            <Link href={supportEmailHref}>{supportEmail}</Link>
            &nbsp;notifying the team that you need additional&nbsp;
            {getForAgent({ case: 'lower' })} seats.
          </li>
        </ul>
        <Alert className={classes.alert} size="small" severity="info">
          Once upgraded, your additional {getForAgent({ case: 'lower' })} seats will be
          ready for assignment
        </Alert>
      </LbBigModal>
    );
  },
);

export { AddSeatsModal };
